import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {z} from 'zod';
import {useAddOrUpdateParam, useDeleteParam, useFetchPresParamValues} from '../queries';
import {IPresentationMeta} from '../../../../../incase-domain/presentation';
import {Param} from '../../../../../incase-domain/presentationParamValues/IPresentationParamValues';
import {createShortPresentationUrl, createShortRoomUrl} from '../../../../../utils/presentation';
import {copyToClipboard} from '../../../../../utils/copyToClipboard';
import {useCreateAutoSlug} from '../../../../../service/hooks/useSlug';
import {useCreateAutoRoomSlug, useListRoomSlugs} from '../../../../../service/hooks/useRoomSlug';
import PresentationLink from './PresentationLink';
import Button from '../../../../../incase-domain/components/button/Button';
import ConfirmModal from '../../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import PresenterLink from './PresenterLink';
import DataView, {ViewMode} from '../../../common/data/DataView';
import InputWithCharacterCounter from '../../../../../components/slides/highlighted/common/InputWithCharacterCounter';
import {getRedirectPresetError, isRedirectParam} from '../../../../../utils/redirect';

interface IPresParamsProps {
    presentation: IPresentationMeta;
    open: boolean;
    roomId?: string;
    presenterLinkCode?: string;
}

function ViewerPresentationLinks({presentation, open, roomId, presenterLinkCode}: IPresParamsProps): JSX.Element {
    const {t} = useTranslation();

    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteModal, setDeleteModal] = useState(false);
    const [param, setParam] = useState<Param[] | null>(null);
    const [viewersLinkWithoutReqParams, setViewersLinkWithoutReqParams] = useState('');
    const [rowLinkLoading, setRowLinkLoading] = useState<string | null>(null);

    const {data: params} = useFetchPresParamValues({
        presentationId: presentation._id,
        enabled: open && !!presentation._id,
    });

    const {data: roomSlugs} = useListRoomSlugs(roomId ?? '');

    const customViewerSlugs = useMemo(() => (roomSlugs ?? []).filter((slug) => !slug.presenterCode), [roomSlugs]);
    const customPresenterSlugs = useMemo(() => (roomSlugs ?? []).filter((slug) => !!slug.presenterCode), [roomSlugs]);

    const paramKeys = useMemo(() => (presentation.requestParams ?? []).map((p) => p.key), [presentation.requestParams]);

    const {mutate: addOrUpdateParam} = useAddOrUpdateParam();
    const {mutate: deleteParam} = useDeleteParam();
    const {mutateAsync: createAutoSlug} = useCreateAutoSlug();
    const {mutateAsync: createAutoRoomSlug} = useCreateAutoRoomSlug();

    const hasRequestParams = !!presentation?.requestParams?.length;

    useEffect(() => {
        if (!open || !presentation._id || hasRequestParams)
            return () => {
                // noop
            };

        let cancelled = false;

        if (roomId) {
            createAutoRoomSlug({roomId})
                .then((slugDoc) => {
                    if (!cancelled) setViewersLinkWithoutReqParams(createShortRoomUrl(slugDoc.slug));
                })
                .catch(() => {
                    if (!cancelled) toast.info(t('presEditPage.subNavBar.failedToCopy'));
                });
        } else {
            createAutoSlug({presentationId: presentation._id})
                .then((slugDoc) => {
                    if (!cancelled) setViewersLinkWithoutReqParams(createShortPresentationUrl(slugDoc.slug));
                })
                .catch(() => {
                    if (!cancelled) toast.info(t('presEditPage.subNavBar.failedToCopy'));
                });
        }

        return () => {
            cancelled = true;
        };
    }, [open, presentation, presentation._id, hasRequestParams, roomId, createAutoSlug, createAutoRoomSlug, t]);

    const schema = useMemo(() => {
        const shape: Record<string, z.ZodTypeAny> = {};
        presentation.requestParams?.forEach((k) => {
            if (isRedirectParam(k.key)) {
                // Always optional; the value becomes a short link preset, so it must be a plain http(s) address
                shape[k.key] = z
                    .string()
                    .trim()
                    .optional()
                    .superRefine((val, ctx) => {
                        const error = val ? getRedirectPresetError(val) : null;
                        if (error) ctx.addIssue({code: z.ZodIssueCode.custom, message: t(`presEditPage.settings.shortLink.${error}`)});
                    });
            } else if (k.required) {
                shape[k.key] = z.string().trim().min(1, 'Required');
            } else {
                shape[k.key] = z.string().trim().optional();
            }
        });
        return z.object(shape);
    }, [presentation.requestParams, t]);

    const validateOne = useCallback(
        (name: string, val: string): boolean => {
            const field = schema.shape[name] as z.ZodString | undefined;
            if (!field) return true;
            const res = field.safeParse(val);
            setErrors((e) => ({...e, [name]: res.success ? '' : res.error.issues[0]?.message ?? 'Invalid'}));
            return res.success;
        },
        [schema]
    );

    const validateAll = useCallback((): boolean => {
        const res = schema.safeParse(values);

        if (res.success) {
            setErrors({});
            return true;
        }
        const flat = res.error.flatten().fieldErrors;
        const next: Record<string, string> = {};

        Object.keys(flat).forEach((k) => {
            next[k] = flat[k]?.[0] ?? 'Invalid';
        });

        setErrors(next);
        return false;
    }, [schema, values]);

    const handleSubmit = useCallback(
        (e?: React.FormEvent): void => {
            e?.preventDefault();
            if (!validateAll()) return;

            addOrUpdateParam({
                presentationId: presentation._id,
                newParam: Object.entries(values).map((el) => ({key: el[0], value: el[1]})),
            });

            setValues((prev) => {
                const cleared = {...prev};
                paramKeys.forEach((k) => {
                    cleared[k] = '';
                });
                return cleared;
            });
            setErrors({});
        },
        [validateAll, addOrUpdateParam, paramKeys, values, presentation._id]
    );

    const handleDelete = (presentationId: string, paramsToDelete: Param[]): void => {
        deleteParam({presentationId, paramsToDelete});
    };

    async function handleCopyLink(copyLink: string): Promise<void> {
        try {
            if (copyLink) await copyToClipboard(copyLink);
            toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
        } catch (err) {
            toast.info(t('presEditPage.subNavBar.failedToCopy'));
        }
    }

    const showPresenterLink = !!roomId;

    const handleRowCopyLink = async (row: Param[]): Promise<void> => {
        const rowKey = row.map((p) => p.key).join(',');
        setRowLinkLoading(rowKey);
        try {
            if (roomId) {
                const slugDoc = await createAutoRoomSlug({roomId, params: row});
                await handleCopyLink(createShortRoomUrl(slugDoc.slug));
            } else {
                const slugDoc = await createAutoSlug({presentationId: presentation._id, params: row});
                await handleCopyLink(createShortPresentationUrl(slugDoc.slug));
            }
        } catch (err) {
            toast.info(t('presEditPage.subNavBar.failedToCopy'));
        } finally {
            setRowLinkLoading(null);
        }
    };

    const renderActions = (row: Param[]): JSX.Element => (
        <div className="u-flex u-gap-1">
            <Button
                trailing="solar:copy-linear"
                variant="soft"
                color="info"
                size="sm"
                loading={rowLinkLoading === row.map((p) => p.key).join(',')}
                onClick={() => handleRowCopyLink(row)}
            >
                {t('editorPage.createPresentationLinksModal.copyLink')}
            </Button>
            <Button
                leading="solar:trash-bin-trash-linear"
                variant="ghost"
                color="error"
                square
                size="sm"
                onClick={() => {
                    setDeleteModal(true);
                    setParam(row);
                }}
            />
        </div>
    );

    const renderRequestParam = (row: Param[], key: string): JSX.Element => {
        const param = row?.find((p) => p.key === key);
        return <span>{param?.value || '-'}</span>;
    };

    const extraRow = useMemo(() => {
        const cells: Record<string, JSX.Element> = {};

        (presentation.requestParams ?? []).forEach((param, index) => {
            const isRedirect = isRedirectParam(param.key);
            cells[param.key] = (
                <div>
                    <InputWithCharacterCounter
                        className="c-sidepanel__input"
                        key={`${param.key}-${index}`}
                        value={values[param.key] ?? ''}
                        placeholder={isRedirect ? t('presEditPage.settings.shortLink.redirectPlaceholder') : param.key}
                        onChange={(v) => {
                            const value = isRedirect ? v.trim() : v;
                            setValues((s) => ({...s, [param.key]: value}));
                            if (errors[param.key]) validateOne(param.key, value);
                        }}
                        onBlur={(v) => validateOne(param.key, v)}
                        enableSubmitByEnter
                        onEnterPress={handleSubmit}
                        validator={() => !errors[param.key]}
                        data-cy={`extra_row_input_${param.key}`}
                    />
                    {isRedirect && errors[param.key] && <div className="c-form-help is-invalid--show">{errors[param.key]}</div>}
                </div>
            );
        });

        cells.actions = (
            <Button
                variant="ghost"
                size="xs"
                color="primary"
                leading="ph:plus"
                onClick={() => handleSubmit()}
                data-cy="extra_row_submit"
                fullWidth
            >
                Add
            </Button>
        );

        return cells;
    }, [values, errors, handleSubmit, validateOne, presentation.requestParams, t]);

    return (
        <>
            <div className="c-viewerPresentationLinks">
                {showPresenterLink && (
                    <div className="c-viewerPresentationLinks__presenter">
                        <Button leading="solar:microphone-2-linear" size="2xl" variant="ghost" disablePointerEvents />
                        <div className="c-viewerPresentationLinks__presenter_title">
                            {t('editorPage.createPresentationLinksModal.presenter')}
                        </div>
                        <div className="c-viewerPresentationLinks__presenter_description">
                            {t('editorPage.createPresentationLinksModal.linkBelowAllowsUserToJoinTheRoomAsPresenter')}
                        </div>

                        <div className="c-viewerPresentationLinks__presenter_button">
                            <PresenterLink presentation={presentation} roomId={roomId} presenterLinkCode={presenterLinkCode} />
                        </div>

                        {customPresenterSlugs.length > 0 && (
                            <div className="c-viewerPresentationLinks__customLinks">
                                <div className="c-viewerPresentationLinks__customLinks_title">
                                    {t('editorPage.createPresentationLinksModal.customShortLinks')}
                                </div>
                                {customPresenterSlugs.map((slug) => (
                                    <div key={slug._id} className="c-viewerPresentationLinks__customLinks_item">
                                        <span className="c-viewerPresentationLinks__customLinks_name">{slug.slug}</span>
                                        <Button
                                            trailing="solar:copy-linear"
                                            variant="soft"
                                            color="info"
                                            size="sm"
                                            onClick={() => handleCopyLink(createShortRoomUrl(slug.slug))}
                                        >
                                            {t('editorPage.createPresentationLinksModal.copyLink')}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <div className="c-viewerPresentationLinks__presenter">
                    <Button leading="solar:users-group-two-rounded-linear" size="2xl" variant="ghost" disablePointerEvents />
                    <div className="c-viewerPresentationLinks__presenter_title">{t('editorPage.createPresentationLinksModal.viewers')}</div>
                    <div className="c-viewerPresentationLinks__presenter_description">
                        {t('editorPage.createPresentationLinksModal.differentiateAndGroupUserReportsByParameters')}
                    </div>

                    {presentation?.requestParams?.length ? (
                        <DataView
                            views={[
                                {
                                    columns: [
                                        ...(presentation.requestParams ?? []).map((requestParameter) => ({
                                            key: requestParameter.key,
                                            body: (row: Param[]) => renderRequestParam(row, requestParameter.key),
                                            label: requestParameter.key,
                                        })),
                                        {
                                            key: 'actions',
                                            body: (row: Param[]) => renderActions(row),
                                            width: '11rem',
                                        },
                                    ],
                                    view: ViewMode.TABLE,
                                },
                            ]}
                            rows={params?.params || []}
                            rowIdentifier={(param) => (param ? param[0].key || '' : '')}
                            extraRow={extraRow}
                        />
                    ) : (
                        <div className="c-viewerPresentationLinks__presenter_button">
                            <PresentationLink link={viewersLinkWithoutReqParams} dataCy="copy_viewer_link" />
                        </div>
                    )}

                    {customViewerSlugs.length > 0 && (
                        <div className="c-viewerPresentationLinks__customLinks">
                            <div className="c-viewerPresentationLinks__customLinks_title">
                                {t('editorPage.createPresentationLinksModal.customShortLinks')}
                            </div>
                            {customViewerSlugs.map((slug) => (
                                <div key={slug._id} className="c-viewerPresentationLinks__customLinks_item">
                                    <span className="c-viewerPresentationLinks__customLinks_name">{slug.slug}</span>
                                    <Button
                                        trailing="solar:copy-linear"
                                        variant="soft"
                                        color="info"
                                        size="sm"
                                        onClick={() => handleCopyLink(createShortRoomUrl(slug.slug))}
                                    >
                                        {t('editorPage.createPresentationLinksModal.copyLink')}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <ConfirmModal
                isOpen={deleteModal}
                color="error"
                onCancel={() => {
                    setDeleteModal(false);
                    setParam(null);
                }}
                onConfirm={() => {
                    if (param) handleDelete(presentation._id, param);
                    setParam(null);
                    setDeleteModal(false);
                }}
                showCancel
                showConfirm
                message={t('editorPage.createPresentationLinksModal.deleteLinkConfirm')}
            />
        </>
    );
}

export default ViewerPresentationLinks;
